import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Send, CheckCircle2, RotateCcw, Clock, ChevronDown, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const fdt = (iso) => { try { return format(new Date(iso), "d MMM · h:mm a"); } catch { return ""; } };
const DECISION = {
  pending: { label: "Pending", cls: "text-muted-foreground", Icon: Clock },
  approved: { label: "Approved", cls: "text-success", Icon: CheckCircle2 },
  returned_for_changes: { label: "Returned", cls: "text-warning", Icon: RotateCcw },
};

// R2-B review surface rendered inside View Topic. Reads live review context from
// the topic payload; explicit actions only — never mutates on stray clicks.
export function TopicReview({ topic, onChanged }) {
  const [mode, setMode] = useState(null);            // null | 'submit' | 'return'
  const [reviewers, setReviewers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockers, setBlockers] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const r = topic.review;

  useEffect(() => {
    if (mode === "submit" && reviewers.length === 0) {
      apiFetch("/topics/meta/reviewers").then(setReviewers).catch(() => {});
    }
  }, [mode, reviewers.length]);

  const submit = async () => {
    if (picked.length === 0) return toast.error("Select at least one reviewer.");
    setBusy(true); setBlockers(null);
    try {
      const fresh = await apiFetch(`/topics/${topic.id}/submit`, { method: "POST", body: JSON.stringify({ reviewer_ids: picked, note }) });
      toast.success("Submitted for review");
      setMode(null); setPicked([]); setNote(""); onChanged?.(fresh);
    } catch (e) {
      if (e.status === 422 && e.data?.detail?.code === "checklist_incomplete") {
        setBlockers(e.data.detail); onChanged?.();   // refresh readiness (instances now exist)
      } else { toast.error(e.message); }
    } finally { setBusy(false); }
  };
  const decide = async (decision) => {
    if (decision === "return" && !feedback.trim()) return toast.error("Feedback is required to return a topic.");
    setBusy(true);
    try {
      const fresh = await apiFetch(`/topics/${topic.id}/review`, { method: "POST", body: JSON.stringify({ decision, feedback }) });
      toast.success(decision === "approve" ? "Approved" : "Returned for changes");
      setMode(null); setFeedback(""); onChanged?.(fresh);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const loadHistory = () => {
    const next = !showHistory; setShowHistory(next);
    if (next && history === null) apiFetch(`/topics/${topic.id}/reviews`).then((d) => setHistory(d.rounds || [])).catch(() => setHistory([]));
  };

  return (
    <div className="border-t border-border/60 pt-6" data-testid="topic-review">
      <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground"><ShieldCheck className="h-4 w-4" /> Review</h2>

      {/* Returned feedback banner */}
      {topic.status === "returned_for_changes" && (topic.returned_feedback || []).length > 0 && (
        <div className="mb-3 rounded-xl border border-warning/40 bg-warning/10 p-3.5" data-testid="topic-returned-banner">
          <p className="flex items-center gap-1.5 text-[13px] font-medium text-warning"><RotateCcw className="h-4 w-4" /> Returned for changes</p>
          {topic.returned_feedback.map((f, i) => (
            <p key={i} className="mt-1.5 break-words text-sm text-foreground/80"><span className="text-muted-foreground">{f.name}:</span> {f.feedback}</p>
          ))}
        </div>
      )}

      {/* Active review round */}
      {r && (
        <div className="rounded-xl border border-border/70 bg-muted/20 p-4" data-testid="topic-review-active">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-foreground">Version {r.version} · {r.approved_count}/{r.required_count} approved</span>
            <span className="text-[12px] text-muted-foreground">Submitted {fdt(r.submitted_at)}{r.submitted_by ? ` by ${r.submitted_by}` : ""}</span>
          </div>
          {r.note && <p className="mt-1.5 break-words text-sm italic text-muted-foreground">“{r.note}”</p>}
          <div className="mt-3 space-y-1.5" data-testid="topic-review-reviewers">
            {r.reviewers.map((rv) => {
              const d = DECISION[rv.decision] || DECISION.pending; const I = d.Icon;
              return (
                <div key={rv.id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-foreground/80">{rv.name}</span>
                  <span className={cn("inline-flex shrink-0 items-center gap-1 text-[12px] font-medium", d.cls)}><I className="h-3.5 w-3.5" /> {d.label}</span>
                </div>
              );
            })}
          </div>

          {r.can_review && mode !== "return" && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => decide("approve")} disabled={busy} className="gap-1.5" data-testid="topic-approve-btn"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</Button>
              <Button size="sm" variant="outline" onClick={() => setMode("return")} disabled={busy} className="gap-1.5" data-testid="topic-return-btn"><RotateCcw className="h-3.5 w-3.5" /> Return for Changes</Button>
            </div>
          )}
          {r.can_review && mode === "return" && (
            <div className="mt-4 space-y-2" data-testid="topic-return-form">
              <Label className="text-xs">Feedback <span className="text-destructive">*</span></Label>
              <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} placeholder="Explain what needs to change…" autoFocus data-testid="topic-return-feedback" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => { setMode(null); setFeedback(""); }} data-testid="topic-return-cancel">Cancel</Button>
                <Button size="sm" onClick={() => decide("return")} disabled={busy || !feedback.trim()} data-testid="topic-return-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Return for Changes"}</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Submit for review */}
      {!r && topic.can_submit && blockers && (
        <div className="mb-3 rounded-xl border border-warning/40 bg-warning/10 p-3.5" data-testid="topic-submit-blockers">
          <p className="flex items-center gap-1.5 text-[13px] font-medium text-warning"><Clock className="h-4 w-4" /> {blockers.message}</p>
          <p className="mt-1 text-[12px] text-muted-foreground">Update the Topic Readiness checklist below, then submit again.</p>
          {(blockers.blocking_items || []).length > 0 && (
            <ul className="mt-2 space-y-0.5 text-[13px] text-foreground/80">
              {blockers.blocking_items.map((b) => <li key={b.item_instance_id}>• {b.title}</li>)}
            </ul>
          )}
        </div>
      )}
      {!r && topic.can_submit && mode !== "submit" && (
        <Button size="sm" onClick={() => setMode("submit")} className="gap-1.5" data-testid="topic-submit-review-btn"><Send className="h-3.5 w-3.5" /> Submit for Review</Button>
      )}
      {!r && topic.can_submit && mode === "submit" && (
        <div className="rounded-xl border border-accent/40 bg-card p-4 shadow-sm" data-testid="topic-submit-form">
          <p className="mb-1 text-sm font-semibold text-foreground">Submit for review</p>
          <p className="mb-3 text-[12px] text-muted-foreground">Current version: {topic.content_version}. Choose who must review this topic.</p>
          <Label className="text-xs">Reviewers <span className="text-destructive">*</span></Label>
          <div className="mt-1.5 max-h-44 space-y-0.5 overflow-y-auto rounded-lg border border-border/60 p-1.5" data-testid="topic-reviewer-list">
            {reviewers.length === 0 ? (
              <p className="p-2 text-[13px] text-muted-foreground">No eligible reviewers are available. Ask an administrator to grant review access.</p>
            ) : reviewers.map((rv) => {
              const on = picked.includes(rv.id);
              return (
                <button key={rv.id} type="button" onClick={() => setPicked((p) => on ? p.filter((x) => x !== rv.id) : [...p, rv.id])}
                  data-testid={`topic-reviewer-${rv.id}`} className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted", on && "text-accent")}>
                  <span className={cn("flex h-4 w-4 items-center justify-center rounded border", on ? "border-accent bg-accent text-accent-foreground" : "border-border")}>{on && <CheckCircle2 className="h-3 w-3" />}</span>
                  {rv.name}
                </button>
              );
            })}
          </div>
          <Label className="mt-3 block text-xs">Note to reviewers <span className="text-muted-foreground/60">(optional)</span></Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Anything reviewers should know…" data-testid="topic-submit-note" />
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { setMode(null); setPicked([]); setNote(""); }} data-testid="topic-submit-cancel">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy || picked.length === 0} data-testid="topic-submit-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button>
          </div>
        </div>
      )}

      {/* Review history */}
      <button onClick={loadHistory} className="mt-4 flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground" data-testid="topic-review-history-toggle">
        {showHistory ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Review History
      </button>
      {showHistory && (
        <div className="mt-2 space-y-2" data-testid="topic-review-history">
          {history === null ? <Loader2 className="h-4 w-4 animate-spin text-accent" />
            : history.length === 0 ? <p className="text-[13px] text-muted-foreground">No reviews yet.</p>
            : history.map((h) => {
              const d = DECISION[h.status === "returned" ? "returned_for_changes" : h.status] || DECISION.pending;
              return (
                <div key={h.id} className="rounded-lg border border-border/60 bg-muted/10 p-3 text-sm" data-testid={`review-round-${h.round_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-foreground">Version {h.version}</span>
                    <span className={cn("text-[12px] font-medium capitalize", d.cls)}>{h.status === "returned" ? "Returned for changes" : h.status}</span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">Submitted {fdt(h.submitted_at)}{h.submitted_by ? ` by ${h.submitted_by}` : ""}</p>
                  {h.reviewers.map((rv, i) => (
                    <div key={i} className="mt-1.5 border-l-2 border-border/60 pl-2.5">
                      <span className="text-[13px] text-foreground/80">{rv.name} — <span className="capitalize">{(rv.decision || "").replace(/_/g, " ")}</span></span>
                      {rv.feedback && <p className="break-words text-[13px] text-muted-foreground">“{rv.feedback}”</p>}
                    </div>
                  ))}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
